export type TerrainEncoding = 'terrarium' | 'mapboxTerrainRGB';

/**
 * Decodes a DEM pixel's channels to metres.
 *
 * Channels must be the raw 0-255 bytes. Canvas `getImageData` gives exactly that, which is why the
 * web has never needed the 256-vs-255 correction the iOS package carried.
 */
export function elevationFromChannels(
  red: number,
  green: number,
  blue: number,
  encoding: TerrainEncoding
): number {
  switch (encoding) {
    case 'terrarium':
      return (red * 256 + green + blue / 256) - 32768;
    case 'mapboxTerrainRGB':
      return -10000 + ((red * 65536 + green * 256 + blue) * 0.1);
  }
}

export interface TerrainTileSource {
  readonly slug: string;
  readonly tileSize: number;
  readonly maximumZoom: number;
  readonly encoding: TerrainEncoding;
  readonly attributionUrl: string;
  tileUrl(z: number, x: number, y: number): string;
}

/**
 * Coupled to the URL rather than chosen separately — pointing a terrarium template at the Mapbox
 * formula yields readings in the hundreds of thousands, and nothing throws.
 *
 * z12 is available globally; z13+ only where higher-resolution source data exists, so the ceiling is
 * the one that never 404s. Matches iOS `TileSource.mapterhorn` and Android `TerrainTileSource.Mapterhorn`.
 */
export const MAPTERHORN: TerrainTileSource = {
  slug: 'mapterhorn',
  tileSize: 512,
  maximumZoom: 12,
  encoding: 'terrarium',
  attributionUrl: 'https://mapterhorn.com/attribution',
  tileUrl: (z, x, y) => `https://tiles.mapterhorn.com/${z}/${x}/${y}.webp`
};

export const MAPBOX_TERRAIN_RGB = (accessToken: string): TerrainTileSource => ({
  slug: 'mapbox-terrain-rgb',
  tileSize: 512,
  maximumZoom: 15,
  encoding: 'mapboxTerrainRGB',
  attributionUrl: 'https://www.mapbox.com/about/maps/',
  tileUrl: (z, x, y) =>
    `https://api.mapbox.com/v4/mapbox.terrain-rgb/${z}/${x}/${y}@2x.pngraw?access_token=${accessToken}`
});
